import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { ROUTES } from '../lib/constants';

export default function ForgotPassword() {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await forgotPassword(email);
      setEmailSent(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // メール送信完了画面
  if (emailSent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg p-8 shadow-lg border border-pink-200 w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher variant="buttons" />
          </div>

          <div className="text-center mb-8">
            <div className="inline-block bg-blue-500 rounded-full p-3 mb-4">
              <span className="text-3xl text-white">📧</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('forgotPassword.emailSent')}</h1>
            <p className="text-gray-600 text-sm">
              {t('forgotPassword.emailSentTo', { email })}
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              {t('forgotPassword.emailInstruction')}
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                setEmailSent(false);
                setEmail('');
              }}
              className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-lg transition-all"
            >
              {t('forgotPassword.resendDifferent')}
            </button>
            <Link
              to={ROUTES.LOGIN}
              className="block w-full py-3 px-4 bg-primary hover:bg-primary-600 text-white font-bold rounded-lg transition-all text-center"
            >
              {t('forgotPassword.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-lg p-8 shadow-lg border border-pink-200 w-full max-w-md">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="buttons" />
        </div>

        <div className="text-center mb-8">
          <div className="inline-block bg-primary rounded-full p-3 mb-4">
            <span className="text-3xl text-white">🔐</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('forgotPassword.title')}</h1>
          <p className="text-gray-600 text-sm">
            {t('forgotPassword.description')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('login.email')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@mail.com"
              required
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-500 text-red-600 px-4 py-3 rounded-lg text-sm font-medium">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-primary hover:bg-primary-600 text-white font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          >
            {isLoading ? tCommon('status.loading') : t('forgotPassword.submit')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to={ROUTES.LOGIN}
            className="text-sm text-primary hover:text-primary-600 transition-colors font-medium"
          >
            ← {t('forgotPassword.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
}
